import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CRDateRangePicker } from './cr-date-range-picker';
import { PresetList } from './preset-list';
import {
  LeftPanel,
  ModalContent,
  RightPanel,
  RightPanelHeader,
  TabButton,
  TabContainer,
  TabContent,
} from './styles';

import { TextInput, TimezoneSelectorV2 } from '@controlrooms/components';
import { TimePresetKeyType, TimeRanges, TimeSelection } from '@controlrooms/models';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export interface TimePreset {
  key: TimePresetKeyType;
  label: string;
  absolute?: {
    startTime: Dayjs;
    endTime: Dayjs;
    timezone?: string;
  };
  relative?: {
    seconds: number;
  };
  keywords?: string[];
  isDefault?: boolean;
}

export interface TimeframePickerProps {
  value: TimeSelection;
  presets: TimePreset[];
  timePresetMap?: Map<TimePresetKeyType, number>;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  includeTime?: boolean;
  allowTimezoneChange?: boolean;
  onDraftChange?: (draft: TimeSelection | null) => void;
  timeFormat?: '24h' | '12h';
  showTimezone?: boolean;
  plantTimezone?: string;
  mode?: 'single' | 'range';
  calendarLayout?: 'single' | 'dual';
  includeSeconds?: boolean;
}

export const TimeframePicker: React.FC<TimeframePickerProps> = ({
  value,
  presets,
  timePresetMap,
  minDate,
  maxDate,
  includeTime = true,
  allowTimezoneChange = true,
  onDraftChange,
  timeFormat = '24h',
  showTimezone = false,
  plantTimezone = 'UTC',
  mode = 'range',
  calendarLayout = 'single',
  includeSeconds = false,
}) => {
  const [draft, setDraft] = useState<TimeSelection>(value);
  const [activeTab, setActiveTab] = useState<'date-range' | 'timezone'>('date-range');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateSelectionMode, setDateSelectionMode] = useState<'start' | 'end'>('start');
  const [startCalendarMonth, setStartCalendarMonth] = useState<Date>(value.startTime.toDate());
  const [endCalendarMonth, setEndCalendarMonth] = useState<Date>(value.endTime.toDate());
  const [sharedHoveredDate, setSharedHoveredDate] = useState<Date | null>(null);

  // Helper function to find matching preset based on duration
  const findMatchingPreset = useCallback(
    (startTime: Dayjs, endTime: Dayjs) => {
      const durationInSeconds = endTime.diff(startTime, 'second');

      return presets.find((preset) => {
        if (preset.relative) {
          return Math.abs(preset.relative.seconds - durationInSeconds) <= 60; // Allow 1 minute tolerance
        }
        return false;
      });
    },
    [presets],
  );

  // Helper function to calculate calendar months for dual layout
  const calculateCalendarMonths = useCallback((startTime: Dayjs, endTime: Dayjs) => {
    const startMonth = startTime.startOf('month').toDate();
    const endMonth = endTime.startOf('month').toDate();

    // Check if both dates are in the same month
    if (startTime.isSame(endTime, 'month')) {
      // Case 2: Same month - show current month and next month
      const nextMonth = startTime.add(1, 'month').startOf('month').toDate();
      return { startMonth, endMonth: nextMonth };
    } else {
      // Case 1: Different months - show start month and end month
      return { startMonth, endMonth };
    }
  }, []);

  // Update calendar months when draft changes
  useEffect(() => {
    if (mode === 'range' && calendarLayout === 'dual') {
      // Convert UTC times to the selected timezone before determining months
      const startTimeInTz = draft.startTime.tz(draft.timezone);
      const endTimeInTz = draft.endTime.tz(draft.timezone);

      // First calendar shows start month, second calendar shows end month (or next month if same)
      const startMonth = startTimeInTz.startOf('month').toDate();
      const endMonth = endTimeInTz.startOf('month').toDate();

      setStartCalendarMonth(startMonth);

      // Only set end calendar month if it's different from start month
      if (!startTimeInTz.isSame(endTimeInTz, 'month')) {
        setEndCalendarMonth(endMonth);
      } else {
        // If same month, show next month in second calendar
        const nextMonth = startTimeInTz.add(1, 'month').startOf('month').toDate();
        setEndCalendarMonth(nextMonth);
      }
    }
  }, [draft.startTime, draft.endTime, draft.timezone, mode, calendarLayout]);

  // Update draft when value changes
  useEffect(() => {
    setDraft(value);

    // Auto-detect preset when timeRange is 'preset'
    if (value.timeRange === TimeRanges.PRESET && !value.timePreset) {
      const matchingPreset = findMatchingPreset(value.startTime, value.endTime);

      if (matchingPreset) {
        const newDraft = new TimeSelection(
          value.startTime,
          value.endTime,
          value.timezone,
          value.streamingTimeInSeconds,
          TimeRanges.PRESET,
          matchingPreset.key,
        );
        newDraft.nowSelected = value.nowSelected;
        setDraft(newDraft);
      }
    }
  }, [value, findMatchingPreset, mode, calendarLayout, calculateCalendarMonths]);

  // Notify parent of draft changes (only when draft differs from incoming value)
  useEffect(() => {
    // Only call onDraftChange if the draft is different from the incoming value
    // This prevents infinite loops when the parent updates the draft
    if (
      draft &&
      (!value || !draft.startTime.isSame(value.startTime) || !draft.endTime.isSame(value.endTime))
    ) {
      let processedDraft = draft;

      // Reset seconds to 00 if includeSeconds is false
      if (!includeSeconds) {
        processedDraft = new TimeSelection(
          draft.startTime.second(0).millisecond(0),
          draft.endTime.second(0).millisecond(0),
          draft.timezone,
          draft.streamingTimeInSeconds,
          draft.timeRange,
          draft.timePreset,
        );
        processedDraft.nowSelected = draft.nowSelected;
      }

      onDraftChange?.(processedDraft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, includeSeconds]);

  const handlePresetSelect = useCallback(
    (preset: TimePreset) => {
      let newDraft: TimeSelection;

      // Get streaming time from TimePresetMap
      const streamingTimeInSeconds = timePresetMap?.get(preset.key) || 0;

      if (preset.absolute) {
        // Use absolute times from preset
        newDraft = new TimeSelection(
          preset.absolute.startTime,
          preset.absolute.endTime,
          preset.absolute.timezone || draft.timezone,
          streamingTimeInSeconds, // Use calculated streaming time
          TimeRanges.PRESET, // Set timeRange to 'preset'
          preset.key, // Set timePreset to the selected preset
        );
      } else if (preset.relative) {
        // Calculate relative times from now
        const now = dayjs().tz(draft.timezone);
        newDraft = new TimeSelection(
          now.subtract(preset.relative.seconds, 'seconds'),
          now,
          draft.timezone,
          streamingTimeInSeconds, // Use calculated streaming time
          TimeRanges.PRESET, // Set timeRange to 'preset'
          preset.key, // Set timePreset to the selected preset
        );
      } else {
        // Fallback to current selection
        newDraft = draft;
      }

      newDraft.nowSelected = false;
      setDraft(newDraft);
    },
    [draft, timePresetMap],
  );

  const handleDateRangeChange = useCallback(
    (dateRange: [Date, Date] | null) => {
      if (dateRange) {
        const [startDate, endDate] = dateRange;

        // Use your constructFinalDraftedTime function logic
        const tz = draft.timezone;

        // Extract the "time of day" from currentDraft in local tz
        const draftStartLocal = draft.startTime.tz(tz);
        const draftEndLocal = draft.endTime.tz(tz);

        // Extract the "date" from dateRange in local tz
        const rangeStartLocal = dayjs.utc(startDate).tz(tz);
        const rangeEndLocal = dayjs.utc(endDate).tz(tz);

        // Determine which dates to use based on mode and selection
        let finalStartLocal: dayjs.Dayjs;
        let finalEndLocal: dayjs.Dayjs;

        if (mode === 'single') {
          // Single date mode: use the selected date for both start and end
          finalStartLocal = rangeStartLocal
            .hour(draftStartLocal.hour())
            .minute(draftStartLocal.minute())
            .second(draftStartLocal.second())
            .millisecond(draftStartLocal.millisecond());
          finalEndLocal = finalStartLocal;
        } else {
          // Range mode: handle individual date selection based on dateSelectionMode
          if (dateSelectionMode === 'start') {
            // Only updating start date - keep existing end date
            finalStartLocal = rangeStartLocal
              .hour(draftStartLocal.hour())
              .minute(draftStartLocal.minute())
              .second(draftStartLocal.second())
              .millisecond(draftStartLocal.millisecond());
            finalEndLocal = draftEndLocal;
          } else if (dateSelectionMode === 'end') {
            // Only updating end date - keep existing start date
            finalStartLocal = draftStartLocal;
            finalEndLocal = rangeEndLocal
              .hour(draftEndLocal.hour())
              .minute(draftEndLocal.minute())
              .second(draftEndLocal.second())
              .millisecond(draftEndLocal.millisecond());
          } else {
            // Fallback: update both dates
            finalStartLocal = rangeStartLocal
              .hour(draftStartLocal.hour())
              .minute(draftStartLocal.minute())
              .second(draftStartLocal.second())
              .millisecond(draftStartLocal.millisecond());
            finalEndLocal = rangeEndLocal
              .hour(draftEndLocal.hour())
              .minute(draftEndLocal.minute())
              .second(draftEndLocal.second())
              .millisecond(draftEndLocal.millisecond());
          }
        }

        // Check if start and end dates are the same day
        const isSameDay = finalStartLocal.isSame(finalEndLocal, 'day');

        // Convert back to UTC
        let newStartTime = finalStartLocal.utc();
        let newEndTime = finalEndLocal.utc();

        // If same day, set start to 00:00:00 and end to 23:59:59
        if (isSameDay) {
          newStartTime = finalStartLocal.startOf('day').utc();
          newEndTime = finalStartLocal.endOf('day').utc();
        }

        const newDraft = new TimeSelection(
          newStartTime,
          newEndTime,
          draft.timezone,
          0, // Clear streaming time for custom selections
          TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually selecting dates
          undefined, // Clear preset when manually selecting dates
        );
        newDraft.nowSelected = false;

        setDraft(newDraft);

        // Don't update calendar months automatically in dual layout
        // Let users navigate months manually

        // Toggle between start and end date selection after each selection
        if (mode === 'range') {
          if (dateSelectionMode === 'start') {
            // After selecting start date, switch to end date selection
            setDateSelectionMode('end');

            // If start date is now greater than end date, clear the end date
            if (newStartTime.isAfter(newEndTime)) {
              newEndTime = newStartTime.add(1, 'hour'); // Set end date to 1 hour after start
              const updatedDraft = new TimeSelection(
                newStartTime,
                newEndTime,
                draft.timezone,
                0, // Clear streaming time for custom selections
                TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually selecting dates
                undefined, // Clear preset when manually selecting dates
              );
              updatedDraft.nowSelected = false;
              setDraft(updatedDraft);
            }
          } else if (dateSelectionMode === 'end') {
            // After selecting end date, switch back to start date selection
            setDateSelectionMode('start');
          }
        }
      }
    },
    [dateSelectionMode, draft.endTime, draft.startTime, draft.timezone, mode],
  );

  const handleTimeChange = useCallback(
    (type: 'start' | 'end', time: string) => {
      // Parse the time string (HH:mm:ss format)
      const [hours, minutes, seconds] = time.split(':').map(Number);

      if (mode === 'single') {
        // In single date mode, both start and end should be the same
        // Create new time in local timezone, then convert to UTC
        const localDate = draft.startTime.tz(draft.timezone);
        const newTime = dayjs
          .tz(
            `${localDate.year()}-${(localDate.month() + 1).toString().padStart(2, '0')}-${localDate
              .date()
              .toString()
              .padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes
              .toString()
              .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
            draft.timezone,
          )
          .utc();
        const newDraft = new TimeSelection(
          newTime,
          newTime, // Same time for both start and end in single mode
          draft.timezone,
          0, // Clear streaming time for custom selections
          TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually editing time
          undefined, // Clear preset when manually editing time
        );
        newDraft.nowSelected = draft.nowSelected;
        setDraft(newDraft);
        return;
      }

      if (type === 'start') {
        // Create new start time in local timezone, then convert to UTC
        const localDate = draft.startTime.tz(draft.timezone);
        const newStartTime = dayjs
          .tz(
            `${localDate.year()}-${(localDate.month() + 1).toString().padStart(2, '0')}-${localDate
              .date()
              .toString()
              .padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes
              .toString()
              .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
            draft.timezone,
          )
          .utc();

        // Check if new start time would be after end time
        if (newStartTime.isAfter(draft.endTime)) {
          // Clear end time to prevent invalid range
          const newDraft = new TimeSelection(
            newStartTime,
            newStartTime, // Set end time same as start time
            draft.timezone,
            0, // Clear streaming time for custom selections
            TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually editing time
            undefined, // Clear preset when manually editing time
          );
          newDraft.nowSelected = draft.nowSelected;
          setDraft(newDraft);
        } else {
          const newDraft = new TimeSelection(
            newStartTime,
            draft.endTime,
            draft.timezone,
            0, // Clear streaming time for custom selections
            TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually editing time
            undefined, // Clear preset when manually editing time
          );
          newDraft.nowSelected = draft.nowSelected;
          setDraft(newDraft);
        }
      } else {
        // Create new end time in local timezone, then convert to UTC
        const localDate = draft.endTime.tz(draft.timezone);
        const newEndTime = dayjs
          .tz(
            `${localDate.year()}-${(localDate.month() + 1).toString().padStart(2, '0')}-${localDate
              .date()
              .toString()
              .padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes
              .toString()
              .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
            draft.timezone,
          )
          .utc();

        // Check if new end time would be before start time
        if (newEndTime.isBefore(draft.startTime)) {
          // Don't allow end time before start time
          return;
        }

        const newDraft = new TimeSelection(
          draft.startTime,
          newEndTime,
          draft.timezone,
          0, // Clear streaming time for custom selections
          TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually editing time
          undefined, // Clear preset when manually editing time
        );
        newDraft.nowSelected = draft.nowSelected;

        setDraft(newDraft);
      }
    },
    [draft, mode],
  );

  // Wrapper function for CRDateRangePicker's onTimeChange prop
  const handleTimeChangeWrapper = useCallback(
    (time: Date) => {
      // Determine which time type is being edited based on current mode and dateSelectionMode
      const type = mode === 'single' ? 'start' : dateSelectionMode === 'start' ? 'start' : 'end';

      // Convert Date to HH:mm:ss format
      const hours = time.getHours().toString().padStart(2, '0');
      const minutes = time.getMinutes().toString().padStart(2, '0');
      const seconds = time.getSeconds().toString().padStart(2, '0');
      const timeString = `${hours}:${minutes}:${seconds}`;

      // Call the original handleTimeChange function
      handleTimeChange(type, timeString);
    },
    [mode, dateSelectionMode, handleTimeChange],
  );

  const handleMonthYearChange = useCallback(
    (date: Date) => {
      if (mode === 'single') {
        // In single date mode, update both start and end to the same date
        const newTime = draft.startTime.year(date.getFullYear()).month(date.getMonth());
        const newDraft = new TimeSelection(
          newTime,
          newTime, // Same date for both start and end in single mode
          draft.timezone,
          0, // Clear streaming time for custom selections
          TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually selecting dates
          undefined, // Clear preset when manually selecting dates
        );
        newDraft.nowSelected = draft.nowSelected;
        setDraft(newDraft);
        return;
      }

      // Range mode: update based on dateSelectionMode
      if (dateSelectionMode === 'start') {
        // Update only start date
        const newStartTime = draft.startTime.year(date.getFullYear()).month(date.getMonth());

        // Check if new start date would be after end date
        if (newStartTime.isAfter(draft.endTime)) {
          // Clear end date to prevent invalid range
          const newDraft = new TimeSelection(
            newStartTime,
            newStartTime, // Set end date same as start date
            draft.timezone,
            0, // Clear streaming time for custom selections
            TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually selecting dates
            undefined, // Clear preset when manually selecting dates
          );
          newDraft.nowSelected = draft.nowSelected;
          setDraft(newDraft);
        } else {
          // Safe to update start date
          const newDraft = new TimeSelection(
            newStartTime,
            draft.endTime,
            draft.timezone,
            0, // Clear streaming time for custom selections
            TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually selecting dates
            undefined, // Clear preset when manually selecting dates
          );
          newDraft.nowSelected = draft.nowSelected;
          setDraft(newDraft);
        }
      } else if (dateSelectionMode === 'end') {
        // Update only end date
        const newEndTime = draft.endTime.year(date.getFullYear()).month(date.getMonth());

        // Check if new end date would be before start date
        if (newEndTime.isBefore(draft.startTime)) {
          // Don't allow end date before start date
          return;
        }

        const newDraft = new TimeSelection(
          draft.startTime,
          newEndTime,
          draft.timezone,
          0, // Clear streaming time for custom selections
          TimeRanges.CUSTOM, // Set timeRange to 'custom' when manually selecting dates
          undefined, // Clear preset when manually selecting dates
        );
        newDraft.nowSelected = draft.nowSelected;
        setDraft(newDraft);
      }
    },
    [draft, mode, dateSelectionMode],
  );

  // Handle month navigation for dual calendar layout
  const handleStartCalendarMonthChange = useCallback((date: Date) => {
    setStartCalendarMonth(date);
    // Don't change dateSelectionMode - let user select both start and end in first calendar
  }, []);

  const handleEndCalendarMonthChange = useCallback((date: Date) => {
    setEndCalendarMonth(date);
    // Don't change dateSelectionMode - second calendar is just for extended view
  }, []);

  const handleTimezoneChange = useCallback(
    (newTimezone: string) => {
      let newStartTime: dayjs.Dayjs;
      let newEndTime: dayjs.Dayjs;

      if (draft.timeRange === TimeRanges.PRESET && draft.streamingTimeInSeconds) {
        // For presets (relative times), recalculate relative to the new timezone
        const nowInNewTimezone = dayjs().tz(newTimezone);
        newStartTime = nowInNewTimezone.subtract(draft.streamingTimeInSeconds, 'seconds').utc();
        newEndTime = nowInNewTimezone.utc();
      } else {
        // For custom (absolute times), preserve the same local date/time values but in the new timezone
        // Convert current UTC times to the old timezone to get local values, then apply to new timezone

        // Get local date/time values in the current timezone
        const startInCurrentTz = draft.startTime.tz(draft.timezone);
        const endInCurrentTz = draft.endTime.tz(draft.timezone);

        // Extract local date/time components
        const startYear = startInCurrentTz.year();
        const startMonth = startInCurrentTz.month();
        const startDate = startInCurrentTz.date();
        const startHour = startInCurrentTz.hour();
        const startMinute = startInCurrentTz.minute();
        const startSecond = startInCurrentTz.second();

        const endYear = endInCurrentTz.year();
        const endMonth = endInCurrentTz.month();
        const endDate = endInCurrentTz.date();
        const endHour = endInCurrentTz.hour();
        const endMinute = endInCurrentTz.minute();
        const endSecond = endInCurrentTz.second();

        // Create new times with the same local values but in the new timezone, then convert to UTC
        newStartTime = dayjs
          .tz(
            `${startYear}-${(startMonth + 1).toString().padStart(2, '0')}-${startDate
              .toString()
              .padStart(2, '0')} ${startHour.toString().padStart(2, '0')}:${startMinute
              .toString()
              .padStart(2, '0')}:${startSecond.toString().padStart(2, '0')}`,
            newTimezone,
          )
          .utc();
        newEndTime = dayjs
          .tz(
            `${endYear}-${(endMonth + 1).toString().padStart(2, '0')}-${endDate
              .toString()
              .padStart(2, '0')} ${endHour.toString().padStart(2, '0')}:${endMinute
              .toString()
              .padStart(2, '0')}:${endSecond.toString().padStart(2, '0')}`,
            newTimezone,
          )
          .utc();
      }

      const newDraft = new TimeSelection(
        newStartTime,
        newEndTime,
        newTimezone,
        draft.streamingTimeInSeconds,
        draft.timeRange,
        draft.timePreset,
      );
      newDraft.nowSelected = draft.nowSelected;

      setDraft(newDraft);
    },
    [draft],
  );

  // Shared hover handlers for dual calendar mode
  const handleSharedMouseEnter = useCallback(
    (date: Date) => {
      if (calendarLayout === 'dual' && dateSelectionMode === 'end' && draft.startTime) {
        setSharedHoveredDate(date);
      }
    },
    [calendarLayout, dateSelectionMode, draft.startTime],
  );

  const handleSharedMouseLeave = useCallback(() => {
    if (calendarLayout === 'dual') {
      setSharedHoveredDate(null);
    }
  }, [calendarLayout]);

  const handleStartTimeChange = useCallback(
    (time: Date) => {
      // Convert UTC Date to local timezone for time extraction
      const timeInLocalTz = dayjs(time).tz(draft.timezone);
      const timeString = `${timeInLocalTz.hour().toString().padStart(2, '0')}:${timeInLocalTz
        .minute()
        .toString()
        .padStart(2, '0')}:${timeInLocalTz.second().toString().padStart(2, '0')}`;

      handleTimeChange('start', timeString);
    },
    [draft.timezone, handleTimeChange],
  );

  const handleEndTimeChange = useCallback(
    (time: Date) => {
      // Convert UTC Date to local timezone for time extraction
      const timeInLocalTz = dayjs(time).tz(draft.timezone);
      const timeString = `${timeInLocalTz.hour().toString().padStart(2, '0')}:${timeInLocalTz
        .minute()
        .toString()
        .padStart(2, '0')}:${timeInLocalTz.second().toString().padStart(2, '0')}`;

      handleTimeChange('end', timeString);
    },
    [draft.timezone, handleTimeChange],
  );

  // Filter presets based on search term
  const filteredPresets = useMemo(() => {
    if (!searchTerm) return presets;

    const searchLower = searchTerm.toLowerCase();
    return presets.filter(
      (preset) =>
        preset.label.toLowerCase().includes(searchLower) ||
        preset.keywords?.some((keyword) => keyword.toLowerCase().includes(searchLower)),
    );
  }, [presets, searchTerm]);

  // Extract timestamps for stable memoization
  const startTimestamp = draft.startTime.valueOf();
  const endTimestamp = draft.endTime.valueOf();

  // Memoize the date array conversion to prevent infinite re-renders
  const dateArray = useMemo((): [Date, Date] => {
    // Pass UTC Date objects to CRDateRangePicker
    // CRDateRangePicker will handle timezone conversion internally
    return [draft.startTime.toDate(), draft.endTime.toDate()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimestamp, endTimestamp]);

  return (
    <ModalContent>
      {mode === 'range' && (
        <LeftPanel>
          <div style={{ padding: '8px' }}>
            <TextInput
              placeholder="Search ..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm((e.target as HTMLInputElement).value);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            />
          </div>
          <PresetList
            presets={filteredPresets}
            selectedPreset={draft.timePreset}
            onPresetSelect={handlePresetSelect}
          />
        </LeftPanel>
      )}

      <RightPanel>
        <RightPanelHeader>
          <TabContainer>
            <TabButton
              onClick={() => {
                setActiveTab('date-range');
                if (mode === 'range') {
                  setDateSelectionMode('start');
                }
              }}
            >
              {mode === 'single'
                ? includeTime
                  ? `${dayjs.utc(draft.startTime).tz(draft.timezone).format('MMM D YY, HH:mm:ss')}`
                  : `${dayjs.utc(draft.startTime).tz(draft.timezone).format('MMM D YY')}`
                : includeTime
                ? `${dayjs
                    .utc(draft.startTime)
                    .tz(draft.timezone)
                    .format('MMM D YY, HH:mm:ss')} - ${dayjs
                    .utc(draft.endTime)
                    .tz(draft.timezone)
                    .format('MMM D YY, HH:mm:ss')}`
                : `${dayjs.utc(draft.startTime).tz(draft.timezone).format('MMM D YY')} - ${dayjs
                    .utc(draft.endTime)
                    .tz(draft.timezone)
                    .format('MMM D YY')}`}
            </TabButton>
            {showTimezone && (
              <TimezoneSelectorV2
                selectedTimezone={draft.timezone}
                onTimezoneChange={handleTimezoneChange}
                plantTimezone={plantTimezone}
                disabled={!allowTimezoneChange}
              />
            )}
          </TabContainer>
        </RightPanelHeader>

        <TabContent>
          {activeTab === 'date-range' && (
            <div style={{ padding: '16px' }}>
              {mode === 'single' ? (
                <div style={{ width: '300px' }}>
                  <CRDateRangePicker
                    value={[draft.startTime.toDate(), draft.startTime.toDate()]}
                    onChange={handleDateRangeChange}
                    minDate={minDate?.toDate()}
                    maxDate={maxDate?.toDate()}
                    showOneCalendar={true}
                    selectionMode="start"
                    onMonthYearChange={handleMonthYearChange}
                    onTimeChange={handleStartTimeChange}
                    currentTimeType="start"
                    timeFormat={timeFormat}
                    disableRangeHighlight={true}
                    includeTime={includeTime}
                    includeSeconds={includeSeconds}
                    timezone={draft.timezone}
                  />
                </div>
              ) : calendarLayout === 'dual' ? (
                <div style={{ display: 'flex', gap: '0px' }}>
                  <div style={{ flex: 1 }}>
                    <CRDateRangePicker
                      value={dateArray}
                      onChange={handleDateRangeChange}
                      minDate={minDate?.toDate()}
                      maxDate={maxDate?.toDate()}
                      showOneCalendar={true}
                      selectionMode={dateSelectionMode}
                      onMonthYearChange={handleStartCalendarMonthChange}
                      onTimeChange={handleStartTimeChange}
                      currentTimeType="start"
                      timeFormat={timeFormat}
                      initialMonth={startCalendarMonth}
                      autoUpdateMonth={false}
                      includeTime={includeTime}
                      includeSeconds={includeSeconds}
                      externalHoveredDate={sharedHoveredDate}
                      onMouseEnter={handleSharedMouseEnter}
                      onMouseLeave={handleSharedMouseLeave}
                      timezone={draft.timezone}
                    />
                  </div>
                  <div
                    style={{
                      width: '1px',
                      backgroundColor: '#e1e5e9',
                      margin: '0 8px',
                      alignSelf: 'stretch',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <CRDateRangePicker
                      value={dateArray}
                      onChange={handleDateRangeChange}
                      minDate={minDate?.toDate()}
                      maxDate={maxDate?.toDate()}
                      showOneCalendar={true}
                      selectionMode={dateSelectionMode}
                      onMonthYearChange={handleEndCalendarMonthChange}
                      onTimeChange={handleEndTimeChange}
                      currentTimeType="end"
                      timeFormat={timeFormat}
                      initialMonth={endCalendarMonth}
                      autoUpdateMonth={false}
                      includeTime={includeTime}
                      includeSeconds={includeSeconds}
                      externalHoveredDate={sharedHoveredDate}
                      onMouseEnter={handleSharedMouseEnter}
                      onMouseLeave={handleSharedMouseLeave}
                      timezone={draft.timezone}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ width: '300px' }}>
                  <CRDateRangePicker
                    value={dateArray}
                    onChange={handleDateRangeChange}
                    minDate={minDate?.toDate()}
                    maxDate={maxDate?.toDate()}
                    showOneCalendar={true}
                    selectionMode={dateSelectionMode}
                    onMonthYearChange={handleMonthYearChange}
                    onTimeChange={handleTimeChangeWrapper}
                    currentTimeType={dateSelectionMode}
                    timeFormat={timeFormat}
                    includeTime={includeTime}
                    includeSeconds={includeSeconds}
                    timezone={draft.timezone}
                  />
                </div>
              )}
            </div>
          )}
        </TabContent>
      </RightPanel>
    </ModalContent>
  );
};
